import { useCallback, useRef, useState } from "react";
import { isSellerIntakeMobileViewport } from "../../../utils/sellerIntake/sellerIntakeFormMobile";
import {
  SELLER_PHOTO_LIMITS,
  createSellerPhotoPreviewEntry,
  revokeSellerPhotoPreviews,
  validateSellerPhotoSelection,
} from "../../../utils/sellerIntake/sellerPhotoValidation";
import { SELLER_INTAKE_FORM_COPY_ES } from "./sellerIntakeFormConstants";

/**
 * @param {{
 *   selectedPhotos?: import('../../../utils/sellerIntake/sellerPhotoValidation').SellerPhotoPreviewEntry[],
 *   onChange?: (photos: import('../../../utils/sellerIntake/sellerPhotoValidation').SellerPhotoPreviewEntry[]) => void,
 *   disabled?: boolean,
 *   errors?: Record<string, string|null|undefined>,
 * }} props
 */
export default function SellerPhotoUploadField({
  selectedPhotos = [],
  onChange,
  disabled = false,
  errors = {},
}) {
  const galleryInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [localError, setLocalError] = useState("");
  const [addingPhotos, setAddingPhotos] = useState(false);
  const showCameraButton = isSellerIntakeMobileViewport();

  const handleFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []);
      if (!files.length) return;

      const validation = validateSellerPhotoSelection(files, selectedPhotos.length);
      if (!validation.ok) {
        setLocalError(validation.message);
        return;
      }

      setAddingPhotos(true);
      setLocalError("");

      try {
        const entries = await Promise.all(files.map((file) => createSellerPhotoPreviewEntry(file)));
        onChange?.([...selectedPhotos, ...entries]);
      } finally {
        setAddingPhotos(false);
      }
    },
    [onChange, selectedPhotos]
  );

  const removePhoto = useCallback(
    (photoId) => {
      const target = selectedPhotos.find((entry) => entry.id === photoId);
      if (target) revokeSellerPhotoPreviews([target]);
      onChange?.(selectedPhotos.filter((entry) => entry.id !== photoId));
    },
    [onChange, selectedPhotos]
  );

  const errorMessage = localError || errors.photos || null;

  return (
    <div className="sell-car-form__photo-upload" data-testid="seller-photo-upload">
      <h3 className="sell-car-form__info-card-title">{SELLER_INTAKE_FORM_COPY_ES.photosSectionTitle}</h3>
      <p>{SELLER_INTAKE_FORM_COPY_ES.photosSectionHelper}</p>
      <ul className="sell-car-form__photo-guidance">
        {SELLER_INTAKE_FORM_COPY_ES.photosGuidance.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      <p className="sell-car-form__photo-limits">
        {SELLER_INTAKE_FORM_COPY_ES.photosLimitsLabel} ({selectedPhotos.length}/{SELLER_PHOTO_LIMITS.maxFiles})
      </p>

      {selectedPhotos.length ? (
        <ul className="sell-car-form__photo-previews" aria-label="Fotos seleccionadas">
          {selectedPhotos.map((photo) => (
            <li key={photo.id} className="sell-car-form__photo-preview-item">
              <img src={photo.previewUrl} alt="" loading="lazy" decoding="async" />
              <button
                type="button"
                className="sell-car-form__photo-remove"
                disabled={disabled}
                onClick={() => removePhoto(photo.id)}
              >
                {SELLER_INTAKE_FORM_COPY_ES.photosRemoveLabel}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="sell-car-form__photo-actions">
        <button
          type="button"
          className="sell-car-page__btn sell-car-page__btn--secondary"
          disabled={disabled || addingPhotos || selectedPhotos.length >= SELLER_PHOTO_LIMITS.maxFiles}
          onClick={() => galleryInputRef.current?.click()}
        >
          {addingPhotos ? SELLER_INTAKE_FORM_COPY_ES.photosAddingLabel : SELLER_INTAKE_FORM_COPY_ES.photosSelectLabel}
        </button>
        {showCameraButton ? (
          <button
            type="button"
            className="sell-car-page__btn sell-car-page__btn--secondary"
            disabled={disabled || addingPhotos || selectedPhotos.length >= SELLER_PHOTO_LIMITS.maxFiles}
            onClick={() => cameraInputRef.current?.click()}
          >
            {SELLER_INTAKE_FORM_COPY_ES.photosCameraLabel}
          </button>
        ) : null}
      </div>

      <input
        ref={galleryInputRef}
        type="file"
        accept={SELLER_PHOTO_LIMITS.accept}
        multiple
        className="sell-car-form__visually-hidden"
        disabled={disabled}
        onChange={(event) => {
          void handleFiles(event.target.files);
          event.target.value = "";
        }}
      />

      <input
        ref={cameraInputRef}
        type="file"
        accept={SELLER_PHOTO_LIMITS.accept}
        capture="environment"
        className="sell-car-form__visually-hidden"
        disabled={disabled}
        onChange={(event) => {
          void handleFiles(event.target.files);
          event.target.value = "";
        }}
      />

      {errorMessage ? (
        <p className="sell-car-form__field-error" role="alert">
          {errorMessage}
        </p>
      ) : null}

      {!errorMessage ? (
        <p className="sell-car-form__photo-note">{SELLER_INTAKE_FORM_COPY_ES.photosTypeHint}</p>
      ) : null}
    </div>
  );
}
